import { z } from 'zod';

/**
 * Nummernkreis-Bereich (ADR 0045, erweitert ADR 0059). Ein Scope pro
 * Beleg-/Entitäts-Art — Gesellschafts-gebundene Scopes bekommen je
 * Gesellschaft eine eigene Sequenz (seit ADR 0059 auch Angebot,
 * Kommissionierauftrag und Einkauf), tenant-weit geteilt bleiben nur noch
 * QM-Prozess, Projekt-Code, Inventur und das Alt-Feature Kommissionierliste.
 */
export const numberSequenceScopeSchema = z.enum([
  'SALES_QUOTE',
  'SALES_ORDER',
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
  'DELIVERY_NOTE',
  'CREDIT_NOTE',
  'PRO_FORMA',
  'PICKING_LIST',
  'PURCHASE_REQUEST',
  'PURCHASE_ORDER',
  'PURCHASE_INVOICE',
  'PICKING_ORDER',
  'PROJECT',
  'QM_PROCESS',
  'INVENTORY_COUNT',
  'TRANSPORT',
  'GOODS_RECEIPT',
  'FEEDBACK_ITEM',
]);
export type NumberSequenceScope = z.infer<typeof numberSequenceScopeSchema>;

/**
 * Scopes, deren Nummern seit ADR 0059 je Gesellschaft gezogen werden. Für sie
 * ist eine „geteilt"-Zeile (Sentinel) ein Alt-Zähler von vor der Umstellung —
 * die Vergabe fasst ihn nicht mehr an. Der Settings-Tab kennzeichnet solche
 * Zeilen als inaktiv, statt sie wie lebende Nummernkreise aussehen zu lassen.
 */
export const ENTITY_BOUND_SEQUENCE_SCOPES: readonly NumberSequenceScope[] = [
  'SALES_QUOTE',
  'SALES_ORDER',
  'INVOICE',
  'ADVANCE_INVOICE',
  'FINAL_INVOICE',
  'DELIVERY_NOTE',
  'CREDIT_NOTE',
  'PRO_FORMA',
  'PURCHASE_REQUEST',
  'PURCHASE_ORDER',
  'PURCHASE_INVOICE',
  'PICKING_ORDER',
];

/**
 * Scopes, aus denen NIE gezogen wird: Anzahlungs- und Schlussrechnung zählen
 * bewusst über den Rechnungs-Kreis (`INVOICE`, ADR 0059 §3 — alle drei sind
 * „auch RE" und müssen lückenlos zusammen zählen). Zeilen dieser Scopes sind
 * Backfill-Artefakte und werden im Settings-Tab als inaktiv gekennzeichnet.
 */
export const RETIRED_SEQUENCE_SCOPES: readonly NumberSequenceScope[] = ['ADVANCE_INVOICE', 'FINAL_INVOICE'];

/**
 * Ist diese Nummernkreis-Zeile von der heutigen Vergabe abgehängt (Alt-Zähler)?
 * `legalEntityId === null` steht für „geteilt" (Sentinel bereits aufgelöst).
 */
export function isRetiredNumberSequence(scope: NumberSequenceScope, legalEntityId: string | null): boolean {
  if (RETIRED_SEQUENCE_SCOPES.includes(scope)) return true;
  return legalEntityId === null && ENTITY_BOUND_SEQUENCE_SCOPES.includes(scope);
}

export const numberSequenceResponseSchema = z.object({
  id: z.string().uuid(),
  scope: numberSequenceScopeSchema,
  /** null = gesellschaftsübergreifend/geteilt (Sentinel-UUID intern, hier bereits aufgelöst). */
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  /** Für die Belegnummern-Vorschau (ADR 0059, `formatNumberSequencePreview`). */
  legalEntityNumberDigit: z.number().int().nullable(),
  year: z.number().int(),
  currentValue: z.number().int(),
  /** Vorschau der nächsten vergebenen Nummer (currentValue + 1). */
  nextValue: z.number().int(),
  /**
   * true = Alt-Zähler, von der heutigen Vergabe abgehängt (geteilte Zeile eines
   * inzwischen Gesellschafts-gebundenen Scopes, oder ADVANCE_/FINAL_INVOICE —
   * die zählen über den Rechnungskreis). Siehe `isRetiredNumberSequence`.
   */
  isRetired: z.boolean(),
  updatedAt: z.string().datetime(),
});
export type NumberSequenceResponse = z.infer<typeof numberSequenceResponseSchema>;

/** Admin-Korrektur des Zählerstands (§11-Anwendungsfall: User-Fehler manuell beheben). */
export const updateNumberSequenceSchema = z.object({
  currentValue: z.number().int().min(0),
});
export type UpdateNumberSequenceInput = z.infer<typeof updateNumberSequenceSchema>;
